/**
 * P1 Your Bank (screens §P1): a full-bleed navy stage, one idea per screen. Welcome → Reveal → Market → Role, then the app.
 * The pre-check between welcome and reveal is a seam: `onWelcomeDone` (WP39) takes over the welcome's Continue.
 */
import { useEffect, useRef, useState } from "react";
import { ROLE_PORTFOLIO, type ContentCatalog, type MarketView, type TeamState, type WelcomeCard } from "@bsim/contracts";
import type { DecisionFamily, KpiId } from "@bsim/domain";
import { formatKpiOrDash } from "../format";
import { firstUseDefinition, kpiShortLabel, labelOf } from "../format/labels";
import { chairOwed } from "../shell/chair";
import { useDeskLabel } from "../shell/desks";
import { roleLabel } from "../shell/TopBar";
import "./bank.css";

export const BANK_STEPS = ["welcome", "reveal", "market", "role"] as const;
export type BankStep = (typeof BANK_STEPS)[number];

const CTA: Readonly<Record<BankStep, string>> = { welcome: "Take the chair", reveal: "Continue", market: "Continue", role: "Continue" };

export interface BankProps {
  readonly welcome: WelcomeCard;
  readonly team: TeamState;
  readonly catalog: ContentCatalog;
  readonly market: MarketView;
  readonly startStep?: BankStep;
  /** WP39's hand-off: called instead of moving to the reveal (it routes to the pre-check and back). */
  readonly onWelcomeDone?: () => void;
  readonly onFinish: () => void;
  /** Called with each step the player moves to, so the page can keep it in the address: a reload or a remount then opens where they were, never back at the reveal. */
  readonly onStep?: (step: BankStep) => void;
  /** The api's sentence when the pre-check was put off (the room is playing a round); read on the reveal. */
  readonly notice?: string;
}

/** One reveal card: the numbers are in the DOM at once; motion only staggers their entrance. */
function Reveal({ index, children, testId }: { readonly index: number; readonly children: React.ReactNode; readonly testId: string }) {
  return <div className="gx-stage-reveal-card" style={{ ["--i" as string]: index }} data-testid={testId}>{children}</div>;
}

function why(catalog: ContentCatalog, kpi: KpiId): string | null {
  return catalog.glossary.find((g) => g.kpi === kpi)?.whyItMatters ?? null;
}

function RevealStep({ kpis, catalog, replay, notice }: { readonly kpis: TeamState["kpiHistory"][number]["kpis"]; readonly catalog: ContentCatalog; readonly replay: boolean; readonly notice?: string | undefined }) {
  const v = (id: KpiId) => formatKpiOrDash(id, kpis[id]);
  const line = (id: KpiId) => { const w = why(catalog, id); return w === null ? null : <p className="gx-stage-reveal-why">{w}</p>; };
  return (
    <>
      <h1 className="gx-stage-h">{replay ? "Replay the reveal: your bank as it was handed to you" : "This is your bank."}</h1>
      {notice === undefined ? null : <p className="gx-stage-note" role="status" data-testid="bank.deferred">{notice}</p>}
      <div className="gx-stage-reveal-grid gx-num">
        <Reveal index={0} testId="bank.reveal.capital">
          <div className="gx-eyebrow">Capital</div>
          <div className="gx-stage-reveal-dual">
            <div><div className="gx-stage-reveal-v" data-testid="bank.reveal.cet1Reported">{v("cet1Reported")}</div><div className="gx-stage-reveal-l">{labelOf("cet1Reported")}</div></div>
            <div><div className="gx-stage-reveal-v" data-testid="bank.reveal.cet1Marked">{v("cet1Marked")}</div><div className="gx-stage-reveal-l">{labelOf("cet1Marked")}</div></div>
          </div>
          {firstUseDefinition("cet1Marked") === null ? null : <p className="gx-stage-reveal-why" data-testid="bank.reveal.cet1Marked.def">{firstUseDefinition("cet1Marked")}</p>}{line("cet1Marked")}
        </Reveal>
        <Reveal index={1} testId="bank.reveal.margin"><div className="gx-eyebrow">{kpiShortLabel("nim")}</div><div className="gx-stage-reveal-v" data-testid="bank.reveal.nim">{v("nim")}</div>{line("nim")}<p className="gx-stage-reveal-why" data-testid="bank.reveal.nim.basis">A run rate: the margin of the last month, annualised.</p></Reveal>
        <Reveal index={2} testId="bank.reveal.returns"><div className="gx-eyebrow">{kpiShortLabel("rotce")}</div><div className="gx-stage-reveal-v" data-testid="bank.reveal.rotce">{v("rotce")}</div>{line("rotce")}</Reveal>
        <Reveal index={3} testId="bank.reveal.value">
          <div className="gx-eyebrow">Share price</div>
          <div className="gx-stage-reveal-dual">
            <div><div className="gx-stage-reveal-v" data-testid="bank.reveal.price">{v("price")}</div><div className="gx-stage-reveal-l">Price</div></div>
            <div><div className="gx-stage-reveal-v" data-testid="bank.reveal.ptbv">{v("ptbv")}</div><div className="gx-stage-reveal-l">P/TBV</div></div>
          </div>
          {line("price")}
        </Reveal>
        <Reveal index={4} testId="bank.reveal.liquidity"><div className="gx-eyebrow">{kpiShortLabel("lcrLite")}</div><div className="gx-stage-reveal-v" data-testid="bank.reveal.lcrLite">{v("lcrLite")}</div>{line("lcrLite")}</Reveal>
        <Reveal index={5} testId="bank.reveal.cre"><div className="gx-eyebrow">Commercial property loans / risk-based capital</div><div className="gx-stage-reveal-v" data-testid="bank.reveal.creToTrbc">{v("creToTrbc")}</div>{line("creToTrbc")}</Reveal>
      </div>
    </>
  );
}

const MARKET_KPIS = ["price", "rotce", "cet1Reported"] as const;

function MarketStep({ market, mySeatId, started }: { readonly market: MarketView; readonly mySeatId: string; readonly started: boolean }) {
  // Before the first year every bank is the same bank: say so once and show one row, instead of the same three figures six times.
  const identical = !started && market.banks.every((b) => MARKET_KPIS.every((k) => b.kpis[k] === market.banks[0]?.kpis[k]));
  const shown = identical ? market.banks.filter((b) => b.seatId === mySeatId).slice(0, 1) : market.banks;
  return (
    <>
      <h1 className="gx-stage-h">{started ? "Your market now. Five other banks, as they stand today." : "Your market. Five other banks, all starting where you are."}</h1>
      {identical ? <p className="gx-stage-note" data-testid="bank.market.same">Every bank starts identical; the differences begin with your first year.</p> : null}
      <div className="gx-stage-market-grid gx-num">
        {shown.map((b, i) => (
          <Reveal key={b.seatId} index={i} testId={`bank.market.${b.seatId}`}>
            <div className={b.seatId === mySeatId ? "gx-stage-market-me" : ""}>
              <div className="gx-stage-market-name">{b.name}</div>
              {b.seatId === mySeatId ? <div className="gx-eyebrow" style={{ color: "var(--gold-on-navy)" }}>You</div> : null}
              <dl className="gx-stage-market-kpis">
                <div><dt>Share price</dt><dd>{formatKpiOrDash("price", b.kpis.price)}</dd></div>
                <div><dt>ROTCE</dt><dd>{formatKpiOrDash("rotce", b.kpis.rotce)}</dd></div>
                <div><dt>CET1</dt><dd>{formatKpiOrDash("cet1Reported", b.kpis.cet1Reported)}</dd></div>
              </dl>
            </div>
          </Reveal>
        ))}
      </div>
    </>
  );
}

function RoleStep({ team }: { readonly team: TeamState }) {
  const desk = useDeskLabel();
  const portfolio = (role: string): readonly string[] => (ROLE_PORTFOLIO as Readonly<Record<string, readonly DecisionFamily[]>>)[role]?.map(desk) ?? [];
  return (
    <>
      <h1 className="gx-stage-h" data-testid="bank.role">You are the {roleLabel(team.me.role)}.</h1>
      <p className="gx-stage-note" data-testid="bank.role.rule">The CEO signs the plan. The risk officer attests it or objects to it first.</p>
      <ul className="gx-stage-team-list">
        {team.members.map((m) => (
          <li key={m.userId} data-testid={`bank.member.${m.userId}`}>
            <span className={`gx-stage-online${m.online ? " gx-stage-online--on" : ""}`} aria-label={m.online ? "online" : "offline"} />
            <b>{m.displayName}</b><span className="gx-stage-team-role">{roleLabel(m.role)}</span>
            {portfolio(m.role).map((f) => <span key={f} className="gx-stage-chip">{f}</span>)}
          </li>
        ))}
      </ul>
    </>
  );
}

export function Bank({ welcome, team, catalog, market, startStep = "welcome", onWelcomeDone, onFinish, onStep, notice }: BankProps) {
  // Once a year has been played, the reveal is a replay of the starting numbers and the market is no longer "all starting where you are".
  const started = team.releasedRounds.length > 0 || (team.round !== null && team.round.state !== "draft");
  const [step, setStep] = useState<BankStep>(startStep);
  // Continue is there at once (R17); the welcome's seconds are a pacing hint, shown as a line, never a lock.
  const [paced, setPaced] = useState(step === "welcome");
  // Once the game is under way the welcome is a reading page: its call to action (take the chair) has nothing left to start.
  const ready = !(step === "welcome" && !chairOwed(team));
  const index = BANK_STEPS.indexOf(step);
  // Continue takes focus so Enter works at once, but never scrolls the page to itself: every step opens at its headline.
  const cta = useRef<HTMLButtonElement>(null);
  useEffect(() => { cta.current?.focus({ preventScroll: true }); document.scrollingElement?.scrollTo?.({ top: 0 }); }, [step, ready]);

  // The welcome runs a progress line for `readSeconds` as a pacing hint; Continue is ready throughout and nothing advances on its own.
  useEffect(() => {
    if (step !== "welcome") { setPaced(false); return; }
    const id = setTimeout(() => setPaced(false), welcome.readSeconds * 1_000);
    return () => clearTimeout(id);
  }, [step, welcome.readSeconds]);

  const next = () => {
    if (step === "welcome" && onWelcomeDone !== undefined) { onWelcomeDone(); return; }
    const following = BANK_STEPS[index + 1];
    if (following === undefined) onFinish(); else { setStep(following); onStep?.(following); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!ready || (e.key !== "Enter" && e.key !== " ")) return;
      if (e.target instanceof HTMLElement && e.target.closest("button, a, input, textarea")) return; // a focused control keeps its own key
      e.preventDefault();
      next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <main className="gx-stage gx-on-navy" data-testid="bank" data-step={step}>
      <div className="gx-stage-body">
        {step === "welcome" ? (
          <>
            <div className="gx-eyebrow gx-stage-eyebrow" data-testid="bank.welcome.eyebrow">Briefing</div>
            <h1 className="gx-stage-title" data-testid="bank.welcome.name">{welcome.bankName}</h1>
            <div className="gx-stage-welcome-copy">{welcome.paragraphs.map((p) => <p key={p}>{p}</p>)}</div>
            <div className="gx-stage-welcome-meta">
              {welcome.role === null || welcome.teamName === null ? null : <span className="gx-stage-pill">{roleLabel(welcome.role)} · {welcome.teamName}</span>}
              <span className="gx-stage-rivals">{welcome.rivalBankNames.join(" · ")}</span>
            </div>
          </>
        ) : null}
        {step === "reveal" ? <RevealStep kpis={team.kpiHistory[0]?.kpis ?? {}} catalog={catalog} replay={started} notice={notice} /> : null}
        {step === "market" ? <MarketStep market={market} mySeatId={team.team.seatId} started={started} /> : null}
        {step === "role" ? <RoleStep team={team} /> : null}
      </div>
      <div className="gx-stage-foot">
        {step === "welcome" && paced && ready ? <div className="gx-stage-progress" aria-hidden="true"><i style={{ animationDuration: `${welcome.readSeconds}s` }} /></div> : null}
        {step === "welcome" && paced && ready ? <p className="gx-stage-note" data-testid="bank.pacing">Take a minute to read. {CTA[step]} is ready.</p> : null}
        <div className="gx-stage-dots" role="img" aria-label={`Step ${index + 1} of ${BANK_STEPS.length}`}>{BANK_STEPS.map((s) => <i key={s} data-on={s === step} />)}</div>
        {ready ? <button type="button" className="gx-btn gx-btn--lg gx-stage-cta" data-testid="bank.continue" onClick={next} ref={cta}>{CTA[step]}</button> : null}
      </div>
    </main>
  );
}
